import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Bell,
  AlertTriangle,
  Clock,
  XCircle,
  Info,
  Search,
  Filter,
  CheckCircle,
  Calendar,
  FileText,
  Truck,
  Users,
  Building2,
  Eye,
  Check,
  X,
} from "lucide-react";
import Swal from "sweetalert2";

const AlertsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  
  // Filtros
  const [filters, setFilters] = useState({
    search: "",
    level: "all",
    entity_type: "all",
    is_read: "all",
  });
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    expired: 0,
    critical: 0,
    warning: 0,
    info: 0,
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  useEffect(() => {
    if (user?.empresa_id) {
      loadAlerts();
      loadStats();
    }
  }, [user?.empresa_id, currentPage, filters]);

  const loadAlerts = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        empresa_id: user.empresa_id,
        page: currentPage,
        per_page: perPage,
      });

      if (filters.level !== "all") {
        params.append("alert_level", filters.level);
      }

      if (filters.entity_type !== "all") {
        params.append("entity_type", filters.entity_type);
      }

      if (filters.is_read !== "all") {
        params.append("unread_only", filters.is_read === "unread" ? "true" : "false");
      }

      const response = await fetch(`${API_URL}/alerts?${params}`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(data.data || []);
        setCurrentPage(data.current_page || 1);
        setTotalPages(data.last_page || 1);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error cargando alertas:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/alerts/summary?empresa_id=${user.empresa_id}`,
        { headers: getHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data.data || {});
      }
    } catch (error) {
      console.error("Error cargando stats:", error);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/alerts/${alertId}/read`, {
        method: "POST",
        headers: getHeaders(),
      });

      if (response.ok) {
        loadAlerts();
        loadStats();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleMarkMultipleAsRead = async () => {
    if (selectedAlerts.length === 0) {
      Swal.fire("Atención", "Selecciona al menos una alerta", "info");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/alerts/mark-multiple`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ alert_ids: selectedAlerts }),
      });

      if (response.ok) {
        Swal.fire("¡Listo!", "Alertas marcadas como leídas", "success");
        setSelectedAlerts([]);
        loadAlerts();
        loadStats();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const toggleSelectAlert = (alertId) => {
    setSelectedAlerts((prev) =>
      prev.includes(alertId)
        ? prev.filter((id) => id !== alertId)
        : [...prev, alertId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedAlerts.length === alerts.length) {
      setSelectedAlerts([]);
    } else {
      setSelectedAlerts(alerts.map((a) => a.id));
    }
  };

  const getAlertIcon = (level) => {
    const icons = {
      info: Info,
      warning: Clock,
      critical: AlertTriangle,
      expired: XCircle,
    };
    return icons[level] || Info;
  };

  const getAlertColor = (level) => {
    const colors = {
      expired: "bg-red-100 text-red-800 border-red-200",
      critical: "bg-orange-100 text-orange-800 border-orange-200",
      warning: "bg-yellow-100 text-yellow-800 border-yellow-200",
      info: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return colors[level] || colors.info;
  };

  const getEntityIcon = (type) => {
    if (type?.includes("Vehiculo")) return Truck;
    if (type?.includes("Conductor")) return Users;
    if (type?.includes("Empresa")) return Building2;
    return FileText;
  };

  const getEntityName = (alert) => {
    const doc = alert.document;
    if (!doc?.documentable) return "Sin información";

    const type = doc.documentable_type || "";
    
    if (type.includes("Vehiculo")) {
      return doc.documentable.placa || "Sin placa";
    }
    if (type.includes("Conductor")) {
      return `${doc.documentable.nombre || ""} ${doc.documentable.apellidos || ""}`.trim();
    }
    if (type.includes("Empresa")) {
      return doc.documentable.nombre_comercial || doc.documentable.razon_social || "Sin nombre";
    }
    
    return "Desconocido";
  };

  const filteredAlerts = alerts.filter((alert) => {
    if (!filters.search) return true;
    
    const searchLower = filters.search.toLowerCase();
    const message = (alert.message || "").toLowerCase();
    const entityName = getEntityName(alert).toLowerCase();
    
    return message.includes(searchLower) || entityName.includes(searchLower);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-md border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-2.5 rounded-lg mr-3">
                <Bell className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Centro de Alertas
                </h1>
                <p className="text-sm text-gray-600">
                  Gestiona todas las notificaciones de documentos de flota
                </p>
              </div>
            </div>

            {selectedAlerts.length > 0 && (
              <button
                onClick={handleMarkMultipleAsRead}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-md flex items-center"
              >
                <Check className="h-4 w-4 mr-2" />
                Marcar {selectedAlerts.length} como leídas
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <StatsCard
            title="Total"
            value={stats.total || 0}
            icon={Bell}
            color="bg-gray-100 text-gray-800"
          />
          <StatsCard
            title="No Leídas"
            value={stats.unread || 0}
            icon={Bell}
            color="bg-blue-100 text-blue-800"
          />
          <StatsCard
            title="Vencidas"
            value={stats.by_level?.expired || 0}
            icon={XCircle}
            color="bg-red-100 text-red-800"
          />
          <StatsCard
            title="Críticas"
            value={stats.by_level?.critical || 0}
            icon={AlertTriangle}
            color="bg-orange-100 text-orange-800"
          />
          <StatsCard
            title="Advertencias"
            value={stats.by_level?.warning || 0}
            icon={Clock}
            color="bg-yellow-100 text-yellow-800"
          />
          <StatsCard
            title="Info"
            value={stats.by_level?.info || 0}
            icon={Info}
            color="bg-blue-100 text-blue-800"
          />
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Búsqueda */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar alertas..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Filtro por nivel */}
            <div>
              <select
                value={filters.level}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, level: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos los niveles</option>
                <option value="expired">⚠️ Vencidas</option>
                <option value="critical">🚨 Críticas</option>
                <option value="warning">⏰ Advertencias</option>
                <option value="info">ℹ️ Informativas</option>
              </select>
            </div>

            {/* Filtro leído/no leído */}
            <div>
              <select
                value={filters.is_read}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, is_read: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas</option>
                <option value="unread">📬 No leídas</option>
                <option value="read">✅ Leídas</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Alertas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando alertas...</p>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No hay alertas</p>
            </div>
          ) : (
            <>
              {/* Header con checkbox */}
              <div className="p-4 border-b bg-gray-50 flex items-center">
                <input
                  type="checkbox"
                  checked={selectedAlerts.length === alerts.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Seleccionar todas ({alerts.length})
                </span>
              </div>

              {/* Lista */}
              <div className="divide-y">
                {filteredAlerts.map((alert) => {
                  const Icon = getAlertIcon(alert.alert_level);
                  const EntityIcon = getEntityIcon(alert.document?.documentable_type);

                  return (
                    <div
                      key={alert.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        !alert.is_read ? "bg-blue-50/30" : ""
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedAlerts.includes(alert.id)}
                          onChange={() => toggleSelectAlert(alert.id)}
                          className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />

                        {/* Icono de nivel */}
                        <div
                          className={`p-3 rounded-lg ${getAlertColor(
                            alert.alert_level
                          )}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 leading-relaxed">
                                {alert.message}
                              </p>

                              {/* Info del documento */}
                              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <EntityIcon className="h-3.5 w-3.5" />
                                  <span>{getEntityName(alert)}</span>
                                </div>

                                <div className="flex items-center gap-1">
                                  <FileText className="h-3.5 w-3.5" />
                                  <span>
                                    {alert.document?.document_type?.name || "N/A"}
                                  </span>
                                </div>

                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" />
                                  <span>
                                    {new Date(alert.alert_date).toLocaleDateString(
                                      "es-PE"
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Indicador no leída */}
                            {!alert.is_read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            )}
                          </div>
                        </div>

                        {/* Botón marcar como leída */}
                        {!alert.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(alert.id)}
                            className="px-3 py-1.5 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="px-4 py-4 bg-gray-50 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Mostrando{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * perPage + 1}
                    </span>{" "}
                    a{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * perPage, total)}
                    </span>{" "}
                    de <span className="font-medium">{total}</span> alertas
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente auxiliar para stats
const StatsCard = ({ title, value, icon: Icon, color }) => (
  <div className={`p-4 rounded-lg ${color} border`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium opacity-75">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>
      <Icon className="h-8 w-8 opacity-50" />
    </div>
  </div>
);

export default AlertsPage;
